import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Footer.css";

function Footer() {
  const [textPages, setTextPages] = useState([]);
  const [contactSettings, setContactSettings] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [success, setSuccess] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTextPages = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/text-pages/`);

        const data = await response.json();
        setTextPages(data);
      } catch (error) {
        console.error("Error fetching text pages:", error);
      }
    };

    const fetchContactSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/contact-settings/`);

        const data = await response.json();
        if (data && data.length > 0) {
          setContactSettings(data[0]);
        }
      } catch (error) {
        console.error("Error fetching contact settings:", error);
      }
    };

    const fetchAudioSetting = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/audio-settings/`);

        const data = await response.json();
        if (data && data.is_enabled === true) {
          setIsAudioEnabled(true);
        }
      } catch (error) {
        console.error("Error fetching audio settings:", error);
      }
    };

    fetchTextPages();
    fetchContactSettings();
    fetchAudioSetting();
  }, []);

  const speakText = (text) => {
    if (!text || !isAudioEnabled) return;
    speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-IN";
    speechSynthesis.speak(utterance);
  };

  const formatWhatsAppMessage = (data) => {
    return `*New Enquiry from SPDTools Website*

*Name:* ${data.name}
*Email:* ${data.email}
*Phone:* ${data.phone}
*Message:* ${data.message}

*Submitted at:* ${new Date().toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  };

  const sendWhatsAppMessage = (enquiryData, skipConfirmation = false) => {
    if (!contactSettings || !contactSettings.whatsapp_number) {
      alert("WhatsApp contact not available. Please try again later.");
      return;
    }

    const message = formatWhatsAppMessage(enquiryData);
    const cleanWhatsAppNumber = contactSettings.whatsapp_number.replace(/\D/g, '');
    
    // Add country code if not present
    const fullNumber = cleanWhatsAppNumber.startsWith('91') 
      ? cleanWhatsAppNumber 
      : `91${cleanWhatsAppNumber}`;
    
    const whatsappUrl = `https://wa.me/${fullNumber}?text=${encodeURIComponent(message)}`;
    
    if (!skipConfirmation) {
      const userConfirmed = window.confirm(
        "Do you want to send this enquiry via WhatsApp as well?\n\n" +
        "This will open WhatsApp with a pre-filled message."
      );
      
      if (!userConfirmed) return;
    }
    
    // Try to detect if it's mobile or desktop
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // On mobile, try the app first, then web
      window.location.href = whatsappUrl;
    } else {
      // On desktop, open WhatsApp Web
      window.open(whatsappUrl, '_blank', 'width=800,height=600');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setSuccess(false);

  // Debug logging
  const apiUrl = process.env.REACT_APP_API_URL;
  console.log('Environment:', process.env.NODE_ENV);
  console.log('API URL:', apiUrl);
  console.log('Full endpoint:', `${apiUrl}api/enquiries/`);

  if (!apiUrl) {
    console.error('REACT_APP_API_URL is not defined');
    alert('Configuration error: API URL not found');
    return;
  }

  try {
    const endpoint = `${apiUrl}api/enquiries/`;
    console.log('Making request to:', endpoint);
    
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    if (response.ok) {
      const responseData = await response.json();
      console.log('Success response:', responseData);
      
      setSuccess(true);
      speakText("Your enquiry has been submitted successfully!");

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);

      // Clear form
      const submittedData = { ...formData };
      setFormData({ name: "", email: "", phone: "", message: "" });

      // Show WhatsApp option after a brief delay
      setTimeout(() => {
        sendWhatsAppMessage(submittedData);
      }, 1000);

    } else {
      const errorData = await response.text();
      console.error("Failed to submit enquiry:", response.status, errorData);
      alert(`Failed to submit enquiry: ${response.status}. Please try again.`);
    }
  } catch (error) {
    console.error("Error submitting enquiry:", error);
    console.error("Error details:", error.message, error.stack);
    alert("Error submitting enquiry. Please check your connection and try again.");
  }
};

  const handleTextPageClick = (page) => {
    const message = `Opening ${page.name}`;
    speechSynthesis.cancel();

    setTimeout(() => {
      speakText(message);
    }, 100);

    const estimatedDelay = message.split(" ").length * 80 + 300;

    setTimeout(() => {
      navigate(`/${page.name.replace(/\s+/g, "-").toLowerCase()}`);
    }, estimatedDelay);
  };

  return (
    <footer className="footer">
      <div className="footer-top">
        <div className="footer-form">
          <h3>Enquiry Form</h3>
          <form onSubmit={handleSubmit}>
            <div>
              <label htmlFor="name">Name:</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onFocus={() => speakText("Enter your name")}
                required
              />
            </div>
            <div>
              <label htmlFor="email">Email:</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => speakText("Enter your email address")}
                required
              />
            </div>
            <div>
              <label htmlFor="phone">Phone:</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onFocus={() => speakText("Enter your phone number")}
                pattern="[0-9]{10}"
                required
              />
            </div>
            <div>
              <label htmlFor="message">Message:</label>
              <textarea
                id="message"
                name="message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                onFocus={() => speakText("Type your message")}
                required
              ></textarea>
            </div>
            <button
              type="submit"
              onFocus={() => speakText("Submit the enquiry form")}
            >
              Submit
            </button>
            
            {success && (
              <div className="success-section">
                <p className="success-msg">
                  ✅ Enquiry submitted successfully!
                </p>
                {contactSettings && contactSettings.whatsapp_number && (
                  <p className="whatsapp-info">
                    📱 WhatsApp message will be sent automatically
                  </p>
                )}
              </div>
            )}
          </form>
        </div>

        <div className="footer-links">
          <h3>Quick Links</h3>
          <ul>
            {textPages.map((page) => (
              <li key={page.id}>
                <span
                  className="footer-link"
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => handleTextPageClick(page)}
                >
                  {page.name}
                </span>
              </li>
            ))}
          </ul>
          
          {/* WhatsApp Contact Info */}
          {contactSettings && contactSettings.whatsapp_number && (
            <div className="whatsapp-contact" style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '5px' }}>
              <h4>📱 WhatsApp Contact</h4>
              <p>+91 {contactSettings.whatsapp_number}</p>
              <button
                onClick={() => {
                  const message = "Hello! I'm interested in your services.";
                  const whatsappUrl = `https://wa.me/91${contactSettings.whatsapp_number.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                }}
                style={{
                  backgroundColor: '#25D366',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Chat on WhatsApp
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="footer-bottom">
        <p>© 2025 SPDTools. All rights reserved.</p>
        <p>
          Site developed by <b>My 727 Infotech</b>. <br />
          <b>CMA T.P.Thangaprabhu</b>, B.Sc., MBA., CIMA ADV DIP MA (UK),
          FCMA., ACPIFSD (IIT - Roorkee) <br />
          <i>Cost Accountant, Full Stack Developer & Data Scientist</i> <br />
          Hand Phone: +91 98430 27787 / 99430 07270 <br />
          Email:{" "}
          <a href="mailto:prabhu@my727infotech.co.in">
            prabhu@my727infotech.co.in
          </a>
        </p>
      </div>
    </footer>
  );
}

export default Footer;